import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Observable, Subscription, finalize } from 'rxjs';
import { CompanyContextService } from '../../../services/company-context.service';
import { FeatureFlagsService } from '../../../services/feature-flags.service';
import { NotificationService } from '../../../../services/notification.service';
import { ApiResponse } from '../../../../shared/types';
import { CheckboxMultiDropdownComponent } from '../../../../shared/components/checkbox-multi-dropdown/checkbox-multi-dropdown.component';
import { InfoTipComponent } from '../../../../shared/components/info-tip';
import { CompanyFlag } from '../../../models/feature-flag.model';
import {
  CompanyFlagRow,
  FEATURE_FLAG_PROPAGATION_NOTE,
  FlagSwitch,
  LOCATION_PICKER_LABELS,
  buildFlagRows,
  buildSwitchPayload,
  locationChanges,
} from '../../../utils/feature-flags.util';

/**
 * CWS-1805: the Features tab on the super-admin company page. One table row per
 * feature: a switch for Way-billed and one for imported memberships when the
 * feature is split by who bills them (CWS-1536), else one switch, and the
 * locations it applies to (a location can only be turned off).
 */
@Component({
  selector: 'app-company-features',
  standalone: true,
  imports: [CommonModule, MatSlideToggleModule, CheckboxMultiDropdownComponent, InfoTipComponent],
  templateUrl: './company-features.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./company-features.component.scss'],
})
export class CompanyFeaturesComponent implements OnInit, OnDestroy {
  private readonly context = inject(CompanyContextService);
  private readonly service = inject(FeatureFlagsService);
  private readonly notifications = inject(NotificationService);

  readonly propagationNote = FEATURE_FLAG_PROPAGATION_NOTE;
  readonly locationLabels = LOCATION_PICKER_LABELS;

  companyId = '';
  flags: CompanyFlag[] = [];
  rows: CompanyFlagRow[] = [];
  loading = false;
  saving = false;
  errorMessage = '';

  private contextSub?: Subscription;
  /** In-flight requests belong to one company; they are dropped when the page switches company. */
  private loadSub?: Subscription;
  private saveSub?: Subscription;

  ngOnInit(): void {
    this.contextSub = this.context.current$.subscribe(company => {
      const nextCompanyId = company?.id || '';
      if (nextCompanyId === this.companyId) {
        return;
      }
      this.cancelRequests();
      this.companyId = nextCompanyId;
      this.setFlags([]);
      if (nextCompanyId) {
        this.load();
      }
    });
  }

  ngOnDestroy(): void {
    this.contextSub?.unsubscribe();
    this.cancelRequests();
  }

  load(): void {
    this.loading = true;
    this.errorMessage = '';
    this.loadSub?.unsubscribe();
    this.loadSub = this.service
      .getCompanyFlags(this.companyId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: res => {
          if (res.status === false) {
            this.errorMessage = res.message || 'Could not load feature flags.';
            return;
          }
          this.setFlags(res.data ?? []);
        },
        error: err => {
          this.errorMessage = err?.error?.message || 'Could not load feature flags.';
        },
      });
  }

  /** The switch is put back at once and only flips when the server answers, so a failed save leaves it as it was. */
  onToggle(event: MatSlideToggleChange, toggle: FlagSwitch): void {
    event.source.checked = toggle.enabled;
    if (toggle.locked) {
      return;
    }
    this.save(() =>
      this.service.updateCompanyFlag(this.companyId, toggle.key, buildSwitchPayload(toggle))
    );
  }

  onLocationsChange(row: CompanyFlagRow, selectedIds: string[]): void {
    const changes = locationChanges(row.flag, selectedIds);
    if (row.locationsLocked || changes.length === 0) {
      return;
    }
    this.save(
      () => this.service.updateLocationFlags(this.companyId, row.flag.key, changes),
      // Earlier locations in the batch may have saved before one failed.
      () => this.load()
    );
  }

  trackRow(_index: number, row: CompanyFlagRow): string {
    return row.key;
  }

  private setFlags(flags: CompanyFlag[]): void {
    this.flags = flags;
    this.rows = buildFlagRows(flags);
  }

  private save(
    request: () => Observable<ApiResponse<CompanyFlag[]>>,
    onFailure?: () => void
  ): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.saveSub = request()
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: res => {
          if (res.status === false) {
            this.notifyError(res.message);
            onFailure?.();
            return;
          }
          this.setFlags(res.data ?? this.flags);
        },
        error: err => {
          this.notifyError(err?.error?.message);
          onFailure?.();
        },
      });
  }

  private cancelRequests(): void {
    this.loadSub?.unsubscribe();
    this.saveSub?.unsubscribe();
  }

  private notifyError(message?: string): void {
    this.notifications.add({
      severity: 'error',
      summary: 'Save failed',
      detail: message || 'Could not update the feature flag.',
    });
  }
}
